import { Tabs, Link } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useSetAtom } from 'jotai';
import { Text, StyleSheet, View, Platform, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import HyzoneMascot from '../../components/HyzoneMascot';
import { useTheme } from '../../contexts/ThemeContext';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import { createPostSheetVisibleAtom } from '../../atoms';
import { getFontFamily, HEADER_LOGO_FONT_FAMILY } from '../../config/fonts';

// Logo component for header
function HeaderLogo() {
  const baseStyle = {
    fontFamily: HEADER_LOGO_FONT_FAMILY,
    fontSize: Platform.OS === 'web' ? 24 : 20,
    ...(Platform.OS === 'web' ? { marginTop: 2 } : {}),
  };

  const content = (
    <View style={styles.logoContainer}>
      <HyzoneMascot width={34} height={34} />
      <View style={styles.textContainer}>
        <Text style={[styles.logo, baseStyle, { color: '#85c363' }]}>HY</Text>
        <Text style={[styles.logo, baseStyle, { color: '#85c363' }]}>ZONE</Text>
      </View>
    </View>
  );

  if (Platform.OS === 'web') {
    return (
      <Link href="/" asChild>
        <Pressable style={({ pressed }) => ({ opacity: pressed ? 0.8 : 1 })}>
          {content}
        </Pressable>
      </Link>
    );
  }

  return content;
}

export default function TabsLayout() {
  const { t } = useTranslation();
  const { colors, isDark } = useTheme();
  const { requireAuth } = useRequireAuth();
  const setCreatePostSheetVisible = useSetAtom(createPostSheetVisibleAtom);

  const fontFamily = getFontFamily();

  return (
    <Tabs
      screenOptions={{
        tabBarActiveTintColor: colors.primary,
        tabBarInactiveTintColor: colors.textSecondary,
        tabBarLabelStyle: fontFamily ? { fontFamily } : undefined,
        tabBarStyle: {
          backgroundColor: colors.tabBar,
          borderTopColor: colors.tabBarBorder,
        },
        headerStyle: {
          backgroundColor: isDark ? colors.background : '#212A35', // Dark navy blue header in light theme
        },
        headerTintColor: colors.text,
        headerShadowVisible: false,
        headerTitle: () => <HeaderLogo />,
      }}
    >
      <Tabs.Screen
        name="index"
        options={{
          title: t('tabs.home'),
          tabBarIcon: ({ color, size, focused }) => (
            <Ionicons name={focused ? 'flame' : 'flame-outline'} size={size} color={color} />
          ),
        }}
      />
      <Tabs.Screen
        name="zones"
        options={{
          title: t('tabs.zones'),
          tabBarIcon: ({ color, size, focused }) => (
            <Ionicons name={focused ? 'compass' : 'compass-outline'} size={size} color={color} />
          ),
        }}
      />
      <Tabs.Screen
        name="create"
        options={{
          title: t('tabs.create'),
          tabBarIcon: ({ color, size, focused }) => (
            <Ionicons name={focused ? 'add-circle' : 'add-circle-outline'} size={size} color={color} />
          ),
        }}
        listeners={{
          tabPress: (e) => {
            // Prevent default navigation
            e.preventDefault();
            // Check authentication before opening the sheet
            if (!requireAuth()) {
              return;
            }
            // Open the create post bottom sheet
            setCreatePostSheetVisible(true);
          },
        }}
      />
      <Tabs.Screen
        name="profile"
        options={{
          title: t('tabs.profile'),
          tabBarIcon: ({ color, size, focused }) => (
            <Ionicons name={focused ? 'person' : 'person-outline'} size={size} color={color} />
          ),
        }}
      />
      <Tabs.Screen
        name="settings"
        options={{
          title: t('tabs.settings'),
          tabBarIcon: ({ color, size, focused }) => (
            <Ionicons name={focused ? 'settings' : 'settings-outline'} size={size} color={color} />
          ),
        }}
      />
    </Tabs>
  );
}

const styles = StyleSheet.create({
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  textContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logo: {
    fontSize: 20,
    fontWeight: '600',
    paddingBottom: 6,
  },
});
